<script setup lang="ts">
import { MarkTypes } from '@storyblok/richtext';
import { type StoryblokRichTextProps } from '@storyblok/vue';
import { Mark } from '@tiptap/core';

// eslint-disable-next-line regex/invalid
import { NuxtLink } from '#components';

// PROPS
type Props = Pick<StoryblokRichTextProps, 'doc'>;
const { doc } = defineProps<Props>();

// METHODS
const convertNodeAttrsToStoryblokMultilink = (attrs: any): StoryblokMultilink => ({
    fieldtype: 'multilink',
    id: attrs?.uuid,
    url: (attrs?.linktype === 'url' || attrs?.linktype === 'asset') && attrs?.href,
    cached_url: attrs?.href,
    target: attrs?.target,
    anchor: attrs?.anchor,
    linktype: attrs?.linktype,
    story: attrs?.story,
    email: attrs?.linktype === 'email' && attrs?.href,
});

// DATA
const CustomLink = Mark.create({
    name: MarkTypes.LINK,
    renderHTML({ HTMLAttributes }) {
        return [
            asTag(NuxtLink),
            { href: storyblokResolveLink(convertNodeAttrsToStoryblokMultilink(HTMLAttributes)), target: HTMLAttributes.target },
            0,
        ];
    },
});
const tiptapExtensions = {
    [MarkTypes.LINK]: CustomLink,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { _raw, ...stats } = useFleequidStats();
const replacements = computed(() => Object.fromEntries(Object.entries(stats).map(([key, value]) => [key, value.value.toString()])));

const applyReplacements = (node: StoryblokRichTextProps['doc'], replacements: Record<string, string>): StoryblokRichTextProps['doc'] => {
    if (node.text) {
        const replaced = Object.entries(replacements).reduce((text, [key, value]) => text.replaceAll(`%${key}%`, value), node.text);

        return { ...node, text: replaced };
    }

    if (node.content) {
        return { ...node, content: node.content.map(child => applyReplacements(child, replacements)) };
    }

    return node;
};

const processedDoc = computed(() => applyReplacements(doc, replacements.value));
</script>

<template>
    <div
        class="prose prose-slate prose-img:rounded-xl w-full [&_[data-type=emoji]>img]:my-0 [&_[data-type=emoji]>img]:inline **:[[id]]:scroll-mt-(--ui-header-height)"
    >
        <StoryblokRichText :doc="processedDoc" :tiptap-extensions="tiptapExtensions" />
    </div>
</template>
